import React, { useState } from 'react';
import { styled } from '@mui/system';
import { Check, X, FileText, Search } from 'lucide-react';
import { Card, Body, ExplainBox, FxLine, Pill } from '../components/ui';
import { motion, fadeUp, popIn, shake } from '../components/motion';
import { L, t } from '../strings';

const Doc = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[100]}`,
  borderRadius: 12,
  overflow: 'hidden',
  margin: '14px 0',
  backgroundColor: '#fff',
  boxShadow: '0 10px 30px rgba(34, 34, 34, 0.07), 0 1px 2px rgba(34, 34, 34, 0.05)',
  ...motion(`${fadeUp} 380ms ease-out 80ms both`)
}));

const DocHead = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 16px',
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  fontWeight: 500,
  '& svg': { color: theme.palette.grey[300], flexShrink: 0 }
}));

const Lives = styled('span')(() => ({
  display: 'inline-flex',
  gap: 4,
  marginLeft: 2,
  '& i': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#79c912',
    transition: 'background-color 250ms ease, transform 250ms ease'
  },
  '& i.used': { backgroundColor: '#ebedee', transform: 'scale(0.8)' }
}));

const Block = styled('button')(({ theme, state, kind }) => {
  const base = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
    textAlign: 'left',
    padding: kind === 'heading' ? '12px 16px 6px' : '10px 16px',
    border: 'none',
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    backgroundColor: '#fff',
    fontFamily: 'Rubik-Regular',
    fontSize: 15,
    lineHeight: '22px',
    cursor: 'pointer',
    color: theme.palette.textPrimary.main,
    transition: 'background-color 250ms ease',
    '&:hover': { backgroundColor: 'rgba(121, 201, 18, 0.06)' },
    '&:disabled': { cursor: 'default', '&:hover': { backgroundColor: '#fff' } }
  };
  if (kind === 'total') Object.assign(base, { fontWeight: 500 });
  if (kind === 'heading')
    Object.assign(base, {
      fontWeight: 500,
      fontSize: 13,
      color: theme.palette.textTertiary.main,
      textTransform: 'uppercase',
      letterSpacing: 0.5
    });
  if (kind === 'note')
    Object.assign(base, { fontStyle: 'italic', color: theme.palette.textSecondary.main });
  if (state === 'found')
    Object.assign(base, {
      backgroundColor: 'rgba(121, 201, 18, 0.12)',
      '&:hover': { backgroundColor: 'rgba(121, 201, 18, 0.12)' }
    });
  if (state === 'missed') Object.assign(base, { backgroundColor: 'rgba(211, 47, 47, 0.10)' });
  if (state === 'wrong')
    Object.assign(base, {
      backgroundColor: theme.palette.grey[50],
      ...motion(`${shake} 380ms ease-in-out`)
    });
  return base;
});

const FakeButton = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '8px 18px',
  borderRadius: 8,
  backgroundColor: theme.palette.primary.salad,
  fontWeight: 500
}));

const Issue = styled('div')(({ bad }) => ({
  fontSize: 13,
  lineHeight: '18px',
  color: bad ? '#b21625' : '#4f9a04',
  marginTop: 4,
  fontWeight: 500,
  transformOrigin: '0 50%',
  ...motion(`${popIn} 300ms ease-out both`)
}));

export default function SpotStep({ step, answered, onSubmit, kpiLabels }) {
  const blocks = step.mock.blocks;
  const issueIds = blocks.filter((b) => b.issue).map((b) => b.id);
  const maxTaps = issueIds.length + 2;
  const [taps, setTaps] = useState([]); // block ids tapped
  const found = taps.filter((id) => issueIds.includes(id));
  const done = !!answered;

  const tap = (id) => {
    if (done || taps.includes(id)) return;
    const next = [...taps, id];
    setTaps(next);
    const f = next.filter((x) => issueIds.includes(x));
    if (f.length === issueIds.length || next.length >= maxTaps) {
      const score = f.length / issueIds.length;
      onSubmit({
        score,
        fx: score >= 0.67 ? step.fx?.ok : step.fx?.fail,
        found: f,
        wrong: next.length - f.length
      });
    }
  };

  const stateOf = (b) => {
    if (taps.includes(b.id)) return b.issue ? 'found' : 'wrong';
    if (done && b.issue) return 'missed';
    return 'idle';
  };

  return (
    <Card>
      <Body>{L(step.text)}</Body>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <Pill key={found.length}>
          <Search size={13} /> {t('found', { f: found.length, n: issueIds.length })}
        </Pill>
        {!done && (
          <Pill title={t('tapsLeft', { n: maxTaps - taps.length })}>
            {t('tapsLeft', { n: maxTaps - taps.length })}
            <Lives aria-hidden="true">
              {Array.from({ length: maxTaps }, (_, i) => (
                <i key={i} className={i >= maxTaps - taps.length ? 'used' : ''} />
              ))}
            </Lives>
          </Pill>
        )}
      </div>
      <Doc>
        <DocHead>
          <FileText size={17} />
          {L(step.mock.title)}
        </DocHead>
        {blocks.map((b) => {
          const st = stateOf(b);
          return (
            <Block
              key={b.id}
              type="button"
              kind={b.kind}
              state={st}
              disabled={done}
              onClick={() => tap(b.id)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {b.kind === 'button' ? (
                  <FakeButton>{L(b.label)}</FakeButton>
                ) : (
                  <span>{L(b.label)}</span>
                )}
                {st === 'found' && (
                  <Issue>
                    <Check size={13} style={{ verticalAlign: -2 }} /> {L(b.issue)}
                  </Issue>
                )}
                {st === 'missed' && (
                  <Issue bad>
                    <X size={13} style={{ verticalAlign: -2 }} /> {t('missed')}: {L(b.issue)}
                  </Issue>
                )}
                {st === 'wrong' && <Issue style={{ color: '#828282' }}>{t('notAnIssue')}</Issue>}
              </div>
              {b.kind !== 'button' && b.kind !== 'heading' && (
                <span style={{ whiteSpace: 'nowrap', fontWeight: b.kind === 'total' ? 500 : 400 }}>
                  {L(b.value)}
                </span>
              )}
            </Block>
          );
        })}
      </Doc>
      {done && (
        <ExplainBox
          ok={answered.score >= 0.67}
          label={t('found', { f: answered.found.length, n: issueIds.length })}>
          {L(step.explain)}
          <FxLine fx={answered.fx} kpiLabels={kpiLabels} />
        </ExplainBox>
      )}
    </Card>
  );
}
